import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, Shield, FileText, ArrowRight, AlertTriangle } from 'lucide-react';

const ExportControls = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Export Controls &<br />Licensing Services
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Navigate complex export control regulations with confidence. Expert guidance on ITAR, EAR, license applications, and jurisdiction determinations to keep your international business moving.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Request Export Control Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Penalties for Export Control Violations Are Severe
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Export control violations carry serious consequences: civil penalties exceeding $300,000 per violation, criminal fines up to $1 million, imprisonment, and denial of export privileges. A single improper export can trigger years of regulatory scrutiny and reputational damage.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our export control solutions provide an accurate, auditable methodology for jurisdiction determination, licensing guidance, and regulatory compliance across ITAR, EAR, and international regimes.
              </p>
            </div>
            <div className="bg-red-50 border-l-4 border-red-600 p-8">
              <AlertTriangle className="h-12 w-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Enforcement Actions</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">$27.5M:</span>
                  <span>ITAR violation for unauthorized defense exports</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">$13M:</span>
                  <span>EAR violation for exports to sanctioned entities</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">5 years:</span>
                  <span>Denial of export privileges for willful violations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Comprehensive Export Control Services
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <Shield className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Jurisdiction Determinations</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Determine whether your products fall under ITAR, EAR, or are uncontrolled. Systematic evaluation using regulatory tests, technical specifications, and end-use analysis.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>ITAR vs. EAR jurisdiction analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Commodity Jurisdiction (CJ) request preparation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Technical data and software classification</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Defense service determination</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <FileText className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Export License Applications</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete preparation and submission of export license applications to BIS and DDTC. Strategic guidance to maximize approval probability and minimize processing time.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>BIS license applications (commodity, temporary import, encryption)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>DDTC license applications (permanent and temporary export)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Technical Assistance Agreements (TAAs)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Manufacturing License Agreements (MLAs)</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <Shield className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">License Exception Qualification</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Identify and leverage available license exceptions to streamline exports while maintaining full compliance with EAR requirements.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Strategic Trade Authorization (STA) eligibility</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Authorized ECCN (TMP, RPL, GOV, TSU, etc.)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Encryption exception (ENC) qualification</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Documentation and recordkeeping requirements</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <FileText className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ITAR Registration & Compliance</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete ITAR registration assistance and ongoing compliance support for manufacturers and exporters of defense articles.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Initial ITAR registration with DDTC</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Technology Control Plan development</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Foreign person access procedures</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>ITAR compliance program implementation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Our Export Control Process
          </h2>

          <div className="space-y-6">
            {[
              {
                num: 1,
                title: "Product & Transaction Evaluation",
                desc: "Comprehensive assessment of products, technical data, software, and proposed transactions. Review of technical specifications, end-users, destinations, and end-uses."
              },
              {
                num: 2,
                title: "Regulatory Framework Analysis",
                desc: "Systematic evaluation against USML, CCL, and applicable international control lists. Application of specifically designed criteria and technical parameters to determine jurisdiction."
              },
              {
                num: 3,
                title: "Licensing Strategy Development",
                desc: "Identification of applicable license requirements, available exceptions, or authorization pathways. Strategic recommendations to optimize approval probability and processing time."
              },
              {
                num: 4,
                title: "Application Preparation & Submission",
                desc: "Complete license application preparation including technical descriptions, justifications, and supporting documentation. Submission through SNAP-R, DTrade, or other required portals."
              },
              {
                num: 5,
                title: "Compliance Integration",
                desc: "Integration of export control determinations into business systems, processes, and documentation. Training of personnel on proper implementation and ongoing compliance."
              }
            ].map((step) => (
              <div key={step.num} className="flex gap-6">
                <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Protect Your Export Privileges
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Partner with experts who understand the complexity and consequences of export control compliance.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Export Control Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExportControls;